import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Loader2, Eye, EyeOff } from 'lucide-react';
import AuthShell from '../components/auth/AuthShell';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';

const Login = () => {
  const { login, googleLogin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [form, setForm] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState(false);

  const from = location.state?.from?.pathname || '/dashboard';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password) return;
    setLoading(true);
    try {
      await login(form);
      toast({ title: 'Welcome back ✨', description: 'Signed in successfully.' });
      navigate(from, { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Login failed';
      toast({ title: 'Sign-in failed', description: msg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Welcome Back"
      subtitle="Sign in to continue your journey."
      footer={<>New here? <Link to="/signup" className="text-mahogany font-medium hover:underline">Create an account</Link></>}
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        <Field label="Email" type="email" name="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
        <div>
          <div className="relative">
            <Field label="Password" type={show ? 'text' : 'password'} name="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} />
            <button type="button" className="absolute right-0 bottom-3 text-mahogany/70" onClick={() => setShow(!show)}>
              {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          <div className="mt-2 text-right">
            <Link to="/forgot-password" className="text-sm text-mahogany hover:underline">Forgot password?</Link>
          </div>
        </div>

        <button type="submit" disabled={loading} className="pill-btn justify-center w-full">
          {loading && <Loader2 className="w-4 h-4 animate-spin" />} Sign In
        </button>
      </form>

      <div className="my-6 flex items-center gap-3 text-mahogany/50 text-xs">
        <span className="flex-1 h-px bg-rose/50" /> OR <span className="flex-1 h-px bg-rose/50" />
      </div>

      <button onClick={googleLogin} className="w-full flex items-center justify-center gap-3 py-3 rounded-full border border-mahogany/25 hover:bg-rose-soft/40 transition-colors">
        <GoogleIcon />
        <span className="text-burgundy font-medium">Continue with Google</span>
      </button>
    </AuthShell>
  );
};

const Field = ({ label, type = 'text', name, value, onChange }) => (
  <label className="block">
    <span className="block text-xs uppercase tracking-[0.2em] text-mahogany/80 mb-2">{label}</span>
    <input
      type={type}
      name={name}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-transparent border-b border-mahogany/30 pb-3 pt-1 text-burgundy focus:outline-none focus:border-mahogany transition-colors"
      required
    />
  </label>
);

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.4 6.1 29.5 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z" />
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16.2 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.4 6.1 29.5 4 24 4 16.1 4 9.2 8.4 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 44c5.4 0 10.3-2 14-5.3l-6.5-5.3C29.4 34.7 26.9 36 24 36c-5.3 0-9.7-3.3-11.3-8l-6.6 5.1C9 39.5 15.9 44 24 44z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.2 5.7l6.5 5.3C40.9 36.6 44 30.7 44 24c0-1.3-.1-2.3-.4-3.5z" />
  </svg>
);

export default Login;
